import { getRecordValue } from 'src/modules/shared/utils/record.util';
import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import type {
  TCWorkflowCreateDefinition,
  WorkflowMetadata,
} from 'src/api/generated';
import { Route as WorkflowDetailRoute } from 'src/routes/tools/workflows/$workflowId.index';
import { Route as ToolsRoute } from 'src/routes/tools.index';
import { useWorkflowVersion } from 'src/modules/shared/hooks/api/automations/workflows/version';
import { useCreateWorkflowMutation } from 'src/modules/shared/hooks/api/automations/workflows/create';
import { useAutomationsWorkflows } from 'src/modules/shared/hooks/api/automations/workflows/workflows';
import type { WorkflowEditorMetadataDraft } from 'src/modules/tools/types/workflow-editor.types';
import type { WorkflowDefinitionDraft } from 'src/modules/tools/utils/workflow-definition.type';

/** Use Workflow Create Params contract. */
interface UseWorkflowCreateParams {
  /** Source workflow used to initialize a new draft. */
  fromWorkflowId?: string;
  /** Current editable workflow definition. */
  definitionDraft?: WorkflowDefinitionDraft;
  /** Current editable workflow input schema. */
  inputSchemaDraft: Record<string, unknown>;
  /** Current editable workflow metadata. */
  metadataDraft: WorkflowEditorMetadataDraft;
  /** Validation failures that prevent saving. */
  inputValidationErrors: string[];
  /** Whether the draft differs from the saved baseline. */
  hasUnsavedChanges: boolean;
  /** Schema used when no input schema has been provided. */
  defaultInputSchema: Record<string, unknown>;
  /** Handles a successfully saved workflow. */
  onSaveSuccess: () => void;
  /** Handles hydration of the draft from an existing workflow. */
  onHydrate: () => void;
  /** Updates the editable workflow definition. */
  setDefinitionDraft: React.Dispatch<
    React.SetStateAction<WorkflowDefinitionDraft>
  >;
  /** Updates the editable workflow input schema. */
  setInputSchemaDraft: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
  /** Updates the editable workflow metadata. */
  setMetadataDraft: React.Dispatch<
    React.SetStateAction<WorkflowEditorMetadataDraft>
  >;
  /** Updates the selected workflow step. */
  setSelectedStepId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

/** Use Workflow Create Result contract. */
interface UseWorkflowCreateResult {
  /** Error displayed when the workflow cannot be saved. */
  formError: string | null;
  /** Returns to the preceding workflow view. */
  handleBack: () => void;
  /** Validates and saves the current workflow draft. */
  handleSave: () => Promise<void>;
  /** Whether a save request is in progress. */
  isSaving: boolean;
  /** Whether the source workflow is still loading. */
  shouldShowSourceLoading: boolean;
  /** Whether loading the source workflow failed. */
  shouldShowSourceError: boolean;
  /** Message describing a source workflow loading failure. */
  sourceErrorMessage: string;
}

export const useWorkflowCreate = ({
  fromWorkflowId,
  definitionDraft,
  inputSchemaDraft,
  metadataDraft,
  inputValidationErrors,
  hasUnsavedChanges,
  defaultInputSchema,
  onSaveSuccess,
  onHydrate,
  setDefinitionDraft,
  setInputSchemaDraft,
  setMetadataDraft,
  setSelectedStepId,
}: UseWorkflowCreateParams): UseWorkflowCreateResult => {
  const navigate = useNavigate();
  const createMutation = useCreateWorkflowMutation();
  const {
    workflowVersion: sourceWorkflowVersion,
    loading: sourceLoading,
    error: sourceError,
  } = useWorkflowVersion(fromWorkflowId, 'DRAFT', Boolean(fromWorkflowId));
  const { workflows: sourceWorkflows } = useAutomationsWorkflows(
    100,
    Boolean(fromWorkflowId)
  );
  const [formError, setFormError] = React.useState<string | null>(null);
  const [shouldNavigateAfterSave, setShouldNavigateAfterSave] =
    React.useState(false);
  const [createdWorkflowId, setCreatedWorkflowId] = React.useState<
    string | null
  >(null);
  const [hydratedFromId, setHydratedFromId] = React.useState<string | null>(
    null
  );
  const sourceMeta = React.useMemo(
    () =>
      fromWorkflowId
        ? sourceWorkflows.find(
            (wf: WorkflowMetadata) => wf.workflowId === fromWorkflowId
          )
        : undefined,
    [fromWorkflowId, sourceWorkflows]
  );

  React.useEffect(() => {
    if (!fromWorkflowId) return;
    if (hydratedFromId === fromWorkflowId) return;
    const sourceDefinition =
      (sourceWorkflowVersion?.definition as
        | WorkflowDefinitionDraft
        | undefined) ?? undefined;
    if (!sourceDefinition) return;

    const clonedDefinition: WorkflowDefinitionDraft =
      structuredClone(sourceDefinition);
    const normalizedDefinition: WorkflowDefinitionDraft = {
      ...clonedDefinition,
      steps: clonedDefinition.steps ?? {},
    };

    setDefinitionDraft(normalizedDefinition);
    setInputSchemaDraft(clonedDefinition.inputSchema ?? defaultInputSchema);
    const sourceTitle = sourceMeta?.name ?? fromWorkflowId;
    setMetadataDraft({
      title: `Copia de ${sourceTitle}`,
      description: sourceMeta?.description ?? '',
    });
    setSelectedStepId(normalizedDefinition.initialStepId);
    onHydrate();
    setHydratedFromId(fromWorkflowId);
  }, [
    defaultInputSchema,
    fromWorkflowId,
    hydratedFromId,
    onHydrate,
    setDefinitionDraft,
    setInputSchemaDraft,
    setMetadataDraft,
    setSelectedStepId,
    sourceMeta?.description,
    sourceMeta?.name,
    sourceWorkflowVersion?.definition,
  ]);

  React.useEffect(() => {
    if (!shouldNavigateAfterSave || hasUnsavedChanges || !createdWorkflowId) {
      return;
    }

    void navigate({
      to: WorkflowDetailRoute.to,
      params: { workflowId: createdWorkflowId },
      search: { phoneVersion: 'v1' },
    });
    setShouldNavigateAfterSave(false);
  }, [createdWorkflowId, hasUnsavedChanges, navigate, shouldNavigateAfterSave]);

  const handleBack = React.useCallback(() => {
    void navigate({
      to: ToolsRoute.to,
      search: { phoneVersion: 'v1' },
    });
  }, [navigate]);

  const handleSave = React.useCallback(async () => {
    if (!definitionDraft) {
      setFormError('No hay definición disponible para guardar.');
      return;
    }
    const steps = definitionDraft.steps ?? {};
    const finalStep = definitionDraft.finalStep;
    const hasAnyStep =
      Object.keys(steps).length > 0 || Boolean(definitionDraft.finalStep);
    if (!hasAnyStep) {
      setFormError('Agrega al menos un paso al flujo antes de guardar.');
      return;
    }
    const initialStepId = definitionDraft.initialStepId;
    if (!initialStepId) {
      setFormError(
        'Selecciona un paso inicial conectando el nodo de inicio con un paso.'
      );
      return;
    }
    if (
      !getRecordValue(steps, initialStepId) &&
      finalStep?.id !== initialStepId
    ) {
      setFormError('El paso inicial debe existir en el flujo.');
      return;
    }
    if (inputValidationErrors.length > 0) {
      setFormError('Corrige los errores en las entradas antes de guardar.');
      return;
    }
    const definitionPayload: TCWorkflowCreateDefinition = {
      inputSchema: inputSchemaDraft,
      initialStepId,
      steps,
      ...(finalStep ? { finalStep } : {}),
    };
    setFormError(null);
    try {
      const cleanDefinition: TCWorkflowCreateDefinition =
        structuredClone(definitionPayload);
      const metadataPayload = {
        name: metadataDraft.title,
        description: metadataDraft.description,
      };
      const cleanMetadata = structuredClone(metadataPayload);
      // Debug log to verify payload matches backend schema expectations.
      console.debug('Creating workflow', {
        definition: cleanDefinition,
        metadata: cleanMetadata,
      });
      const created = await createMutation.mutateAsync({
        body: {
          definition: cleanDefinition,
          metadata: cleanMetadata,
        },
      });
      onSaveSuccess();
      const nextWorkflowId =
        nonEmptyString(created.metadata.workflowId) ??
        created.draftVersion.workflowId;
      if (!nextWorkflowId) {
        setFormError('No se pudo obtener el ID del flujo creado.');
        return;
      }
      setCreatedWorkflowId(nextWorkflowId);
      setShouldNavigateAfterSave(true);
    } catch (mutationError) {
      setFormError(
        mutationError instanceof Error
          ? mutationError.message
          : 'Error al guardar el flujo.'
      );
    }
  }, [
    createMutation,
    definitionDraft,
    inputSchemaDraft,
    inputValidationErrors.length,
    metadataDraft.description,
    metadataDraft.title,
    onSaveSuccess,
  ]);

  const isCloningFromSource = Boolean(fromWorkflowId);
  const shouldShowSourceLoading =
    isCloningFromSource && !hydratedFromId && sourceLoading;
  const shouldShowSourceError =
    isCloningFromSource &&
    !hydratedFromId &&
    (Boolean(sourceError) || (!sourceLoading && !sourceWorkflowVersion));
  const sourceErrorMessage =
    nonEmptyString(sourceError?.message) ??
    'No se pudo cargar el flujo de origen.';

  return {
    formError,
    handleBack,
    handleSave,
    isSaving: createMutation.isPending,
    shouldShowSourceLoading,
    shouldShowSourceError,
    sourceErrorMessage,
  };
};
